import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import SignatureCanvas from 'react-signature-canvas';
import { adminAgreementAPI } from '../../services/agreementAPI';
import './AgreementsAdmin.css';

interface Template {
  id: number;
  type: string;
  version: string;
  title: string;
  content: string;
  content_hash: string;
  is_active: boolean;
  requires_witness: boolean;
  created_at: string;
}

interface SignedAgreement {
  id: string;
  template_id: number;
  template_title: string;
  template_type: string;
  template_version: string;
  signer_name: string;
  signer_email: string;
  external_company: string;
  status: string;
  signed_at: string | null;
  countersigned_at: string | null;
  countersigned_by: string | null;
  has_pdf: boolean;
  emailed_at: string | null;
  created_at: string;
  // detail fields
  signature_image?: string;
  countersignature_image?: string;
  content_hash_at_signing?: string;
  ip_address?: string;
  external_address?: string;
  template_content?: string;
  audit_logs?: { action: string; performed_by: string | null; timestamp: string; metadata: any }[];
}

interface DashboardData {
  total: number;
  signed: number;
  pending: number;
  this_month: number;
  by_type: { template__type: string; count: number }[];
}

type Tab = 'dashboard' | 'templates' | 'signed' | 'sign-now';

const TYPE_LABELS: Record<string, string> = {
  platform: 'Platform Agreement',
  egg_watch: 'Egg Watch Addendum',
  demo_nda: 'Demo NDA',
  contractor_nda: 'Contractor NDA',
  retail_partner: 'Retail Partner',
};

const AgreementsAdmin: React.FC = () => {
  const [tab, setTab] = useState<Tab>('dashboard');

  return (
    <div className="agreements-admin">
      <h1>Agreements</h1>
      <div className="agreements-tabs">
        {(['dashboard', 'templates', 'signed', 'sign-now'] as Tab[]).map(t => (
          <button
            key={t}
            className={`agreements-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'sign-now' ? 'Sign Now' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'signed' && <SignedTab />}
      {tab === 'sign-now' && <SignNowTab />}
    </div>
  );
};

// ─── Dashboard Tab ──────────────────────────────────────────

const DashboardTab: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    adminAgreementAPI.getDashboard().then(setData).catch(console.error);
  }, []);

  if (!data) return <div className="agreements-empty">Loading...</div>;

  return (
    <div>
      <div className="agreements-stats">
        <div className="agreements-stat-card">
          <div className="stat-number">{data.total}</div>
          <div className="stat-label">Total Agreements</div>
        </div>
        <div className="agreements-stat-card">
          <div className="stat-number">{data.signed}</div>
          <div className="stat-label">Signed</div>
        </div>
        <div className="agreements-stat-card">
          <div className="stat-number">{data.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="agreements-stat-card">
          <div className="stat-number">{data.this_month}</div>
          <div className="stat-label">This Month</div>
        </div>
      </div>

      {data.by_type.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ fontSize: '0.95rem', color: '#333' }}>By Type</h3>
          <table className="agreements-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {data.by_type.map(item => (
                <tr key={item.template__type}>
                  <td>{TYPE_LABELS[item.template__type] || item.template__type}</td>
                  <td>{item.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Templates Tab ──────────────────────────────────────────

const TemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ type: 'demo_nda', version: '', title: '', content: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminAgreementAPI.getTemplates().then(setTemplates).catch(console.error);
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const created = await adminAgreementAPI.createTemplate(form);
      setTemplates(prev => [created, ...prev]);
      setShowCreate(false);
      setForm({ type: 'demo_nda', version: '', title: '', content: '', is_active: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="agreements-btn-row" style={{ marginBottom: '1rem' }}>
        <button className="agreements-btn agreements-btn-primary" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Template'}
        </button>
      </div>

      {showCreate && (
        <div className="agreements-form" style={{ marginBottom: '1.5rem' }}>
          {error && <div className="agreements-error">{error}</div>}
          <div className="agreements-form-row">
            <label>Type</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="agreements-form-row">
            <label>Version</label>
            <input value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} placeholder="e.g. 1.0" />
          </div>
          <div className="agreements-form-row">
            <label>Title</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Agreement title" />
          </div>
          <div className="agreements-form-row">
            <label>Content (HTML)</label>
            <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="<h2>Terms</h2><p>...</p>" />
          </div>
          <div className="agreements-btn-row">
            <button className="agreements-btn agreements-btn-primary" onClick={handleCreate} disabled={saving || !form.version || !form.title || !form.content}>
              {saving ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </div>
      )}

      <table className="agreements-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Type</th>
            <th>Version</th>
            <th>Active</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {templates.length === 0 ? (
            <tr><td colSpan={5} className="agreements-empty">No templates yet</td></tr>
          ) : templates.map(t => (
            <tr key={t.id}>
              <td>{t.title}</td>
              <td>{TYPE_LABELS[t.type] || t.type}</td>
              <td>v{t.version}</td>
              <td>{t.is_active ? 'Yes' : 'No'}</td>
              <td>{new Date(t.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Signed Tab ─────────────────────────────────────────────

const SignedTab: React.FC = () => {
  const [items, setItems] = useState<SignedAgreement[]>([]);
  // Server search: input binds to `searchInput` (instant); debounced `search` (400ms)
  // drives the agreements fetch so typing fires one request after the pause.
  const { value: searchInput, setValue: setSearchInput, debounced: search } = useDebouncedSearch('', 400);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState<SignedAgreement | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await adminAgreementAPI.getSignedList(params);
      setItems(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: string) => {
    try {
      const data = await adminAgreementAPI.getSignedDetail(id);
      setDetail(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendEmail = async (id: string) => {
    try {
      await adminAgreementAPI.sendEmail(id);
      alert('Email sent!');
      if (detail) openDetail(id);
    } catch (err) {
      alert('Failed to send email');
    }
  };

  if (detail) {
    return (
      <div className="agreement-detail">
        <button className="agreements-btn agreements-btn-secondary" onClick={() => setDetail(null)}>
          Back to list
        </button>

        <h2 style={{ marginTop: '1rem' }}>{detail.template_title}</h2>

        <div className="meta-grid" style={{ marginTop: '0.75rem' }}>
          <span className="meta-label">Status</span>
          <span><span className={`agreement-status ${detail.status}`}>{detail.status}</span></span>
          <span className="meta-label">Signer</span>
          <span>{detail.signer_name}</span>
          <span className="meta-label">Email</span>
          <span>{detail.signer_email || '-'}</span>
          <span className="meta-label">Company</span>
          <span>{detail.external_company || '-'}</span>
          <span className="meta-label">Address</span>
          <span>{detail.external_address || '-'}</span>
          <span className="meta-label">Signed At</span>
          <span>{detail.signed_at ? new Date(detail.signed_at).toLocaleString() : '-'}</span>
          <span className="meta-label">IP Address</span>
          <span>{detail.ip_address || '-'}</span>
          <span className="meta-label">Content Hash</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{detail.content_hash_at_signing || '-'}</span>
          <span className="meta-label">Countersigned</span>
          <span>{detail.countersigned_by ? `${detail.countersigned_by} at ${new Date(detail.countersigned_at!).toLocaleString()}` : 'Not yet'}</span>
          <span className="meta-label">Emailed</span>
          <span>{detail.emailed_at ? new Date(detail.emailed_at).toLocaleString() : 'Not yet'}</span>
        </div>

        {detail.signature_image && (
          <>
            <h3>Signer Signature</h3>
            <img src={detail.signature_image} alt="Signature" className="sig-img" />
          </>
        )}

        {detail.countersignature_image && (
          <>
            <h3>Countersignature</h3>
            <img src={detail.countersignature_image} alt="Countersignature" className="sig-img" />
          </>
        )}

        <div className="agreements-btn-row">
          {detail.status === 'signed' && !detail.countersigned_at && (
            <button className="agreements-btn agreements-btn-primary" onClick={() => handleCountersign(detail.id)}>
              Countersign
            </button>
          )}
          {detail.signer_email && (
            <button className="agreements-btn agreements-btn-secondary" onClick={() => handleSendEmail(detail.id)}>
              {detail.emailed_at ? 'Resend Email' : 'Email Signed PDF'}
            </button>
          )}
        </div>

        {detail.audit_logs && detail.audit_logs.length > 0 && (
          <>
            <h3>Audit Log</h3>
            <div className="audit-log">
              {detail.audit_logs.map((log, i) => (
                <div className="audit-log-entry" key={i}>
                  <span className="audit-action">{log.action}</span>
                  <span>{log.performed_by || 'System'}</span>
                  <span className="audit-time">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  const handleCountersign = async (id: string) => {
    try {
      await adminAgreementAPI.countersign(id);
      load();
    } catch (err) {
      alert('Failed to countersign');
    }
  };

  return (
    <div>
      <div className="agreements-toolbar">
        <input
          placeholder="Search by name, email, company..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="signed">Signed</option>
          <option value="pending">Pending</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {loading && items.length === 0 ? (
        <div className="agreements-empty">Loading...</div>
      ) : (
        <table className="agreements-table">
          <thead>
            <tr>
              <th>Signer</th>
              <th>Agreement</th>
              <th>Status</th>
              <th>Signed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="agreements-empty">No agreements found</td></tr>
            ) : items.map(item => (
              <tr key={item.id}>
                <td>
                  <div>{item.signer_name}</div>
                  {item.external_company && <div style={{ fontSize: '0.75rem', color: '#888' }}>{item.external_company}</div>}
                </td>
                <td>{item.template_title} v{item.template_version}</td>
                <td><span className={`agreement-status ${item.status}`}>{item.status}</span></td>
                <td>{item.signed_at ? new Date(item.signed_at).toLocaleDateString() : '-'}</td>
                <td>
                  <button className="agreements-btn agreements-btn-secondary" style={{ padding: '2px 8px', fontSize: '0.8rem' }} onClick={() => openDetail(item.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Sign Now Tab ───────────────────────────────────────────

type SigningStep = 'setup' | 'signing' | 'return-device' | 'countersign' | 'done';

const SignNowTab: React.FC = () => {
  const [step, setStep] = useState<SigningStep>('setup');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  // Setup form
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerCompany, setSignerCompany] = useState('');
  const [signerAddress, setSignerAddress] = useState('');

  // Signing session
  const [agreementId, setAgreementId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const sigRef = useRef<SignatureCanvas>(null);

  useEffect(() => {
    adminAgreementAPI.getTemplates().then(t => {
      setTemplates(t.filter((tp: Template) => tp.is_active));
    }).catch(console.error);
  }, []);

  const handleStartSigning = async () => {
    if (!templateId) return;
    setError('');
    try {
      const result = await adminAgreementAPI.createSigning({
        template_id: templateId as number,
        name: signerName,
        email: signerEmail,
        company: signerCompany,
        address: signerAddress,
      });
      setAgreementId(result.id);
      setSelectedTemplate(result.template);
      setStep('signing');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create signing session');
    }
  };

  const handleSubmitSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please sign before submitting.');
      return;
    }
    setError('');
    try {
      const signature = sigRef.current.toDataURL('image/png');
      await adminAgreementAPI.submitSignature(agreementId, { signature, name: signerName });
      setStep('return-device');
    } catch (err: any) {
      setError(err.message || 'Failed to submit signature');
    }
  };

  const handleCountersign = async () => {
    setError('');
    try {
      await adminAgreementAPI.countersign(agreementId);
      setStep('done');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to countersign');
    }
  };

  const handleEmailNow = async () => {
    try {
      await adminAgreementAPI.sendEmail(agreementId);
      setMessage('Email sent successfully!');
    } catch {
      setError('Failed to send email');
    }
  };

  const handleReset = () => {
    setStep('setup');
    setTemplateId('');
    setSignerName('');
    setSignerEmail('');
    setSignerCompany('');
    setSignerAddress('');
    setAgreementId('');
    setSelectedTemplate(null);
    setError('');
    setMessage('');
  };

  // ─── Setup Step ─────────────────────

  if (step === 'setup') {
    return (
      <div className="agreements-form">
        <h3 style={{ marginTop: 0 }}>Prepare Signing Session</h3>
        {error && <div className="agreements-error">{error}</div>}

        <div className="agreements-form-row">
          <label>Agreement Template</label>
          <select value={templateId} onChange={e => setTemplateId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Select a template...</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.title} (v{t.version})</option>
            ))}
          </select>
        </div>
        <div className="agreements-form-row">
          <label>Signer Name</label>
          <input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Full legal name" />
        </div>
        <div className="agreements-form-row">
          <label>Signer Email</label>
          <input type="email" value={signerEmail} onChange={e => setSignerEmail(e.target.value)} placeholder="Email for signed copy" />
        </div>
        <div className="agreements-form-row">
          <label>Company</label>
          <input value={signerCompany} onChange={e => setSignerCompany(e.target.value)} placeholder="Company or farm name" />
        </div>
        <div className="agreements-form-row">
          <label>Address</label>
          <input value={signerAddress} onChange={e => setSignerAddress(e.target.value)} placeholder="Mailing address" />
        </div>

        <div className="agreements-btn-row">
          <button className="agreements-btn agreements-btn-primary" onClick={handleStartSigning} disabled={!templateId}>
            Present for Signing
          </button>
        </div>
      </div>
    );
  }

  // ─── Signing Step (full-screen) ─────

  if (step === 'signing') {
    return (
      <div className="signing-mode-overlay">
        <div className="signing-mode-header">
          <h2>{selectedTemplate?.title || 'Agreement'}</h2>
          <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>Please read and sign below</span>
        </div>
        <div className="signing-mode-body">
          {error && <div className="agreements-error">{error}</div>}

          {selectedTemplate && (
            <div className="nda-scroll-box" dangerouslySetInnerHTML={{ __html: selectedTemplate.content }} />
          )}

          <h3 style={{ marginTop: '1rem' }}>Your Signature</h3>
          <div className="signing-sig-canvas-wrapper">
            <SignatureCanvas
              ref={sigRef}
              canvasProps={{ style: { width: '100%', height: '150px', display: 'block' } }}
              penColor="#333"
            />
            <button type="button" className="signing-sig-clear" onClick={() => sigRef.current?.clear()}>
              Clear
            </button>
          </div>

          <div className="agreements-btn-row" style={{ justifyContent: 'center' }}>
            <button className="agreements-btn agreements-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }} onClick={handleSubmitSignature}>
              Submit Signature
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Return Device Step ────────────

  if (step === 'return-device') {
    return (
      <ReturnDeviceScreen onVerified={() => setStep('countersign')} />
    );
  }

  // ─── Countersign Step ──────────────

  if (step === 'countersign') {
    return (
      <div className="agreements-form">
        <h3 style={{ marginTop: 0 }}>Countersign Agreement</h3>
        <p style={{ color: '#666', fontSize: '0.85rem' }}>
          {signerName} has signed. Add your countersignature to finalize.
        </p>
        {error && <div className="agreements-error">{error}</div>}

        <div className="agreements-btn-row">
          <button className="agreements-btn agreements-btn-primary" onClick={handleCountersign}>
            Countersign with Saved Signature
          </button>
        </div>
      </div>
    );
  }

  // ─── Done Step ─────────────────────

  return (
    <div className="agreements-form">
      <h3 style={{ marginTop: 0 }}>Agreement Complete</h3>
      {message && <div className="agreements-success">{message}</div>}
      {error && <div className="agreements-error">{error}</div>}
      <p style={{ color: '#666', fontSize: '0.85rem' }}>
        The agreement has been signed and countersigned. A PDF has been generated.
      </p>
      <div className="agreements-btn-row">
        {signerEmail && (
          <button className="agreements-btn agreements-btn-primary" onClick={handleEmailNow}>
            Email Signed PDF Now
          </button>
        )}
        <button className="agreements-btn agreements-btn-secondary" onClick={handleReset}>
          Start Another
        </button>
      </div>
    </div>
  );
};

// ─── Return Device Screen (PIN-locked) ─────────────────────

const ReturnDeviceScreen: React.FC<{ onVerified: () => void }> = ({ onVerified }) => {
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  const handleVerifyPin = async () => {
    setPinError('');
    try {
      const result = await adminAgreementAPI.verifyPin(pin);
      if (result.verified) {
        onVerified();
      } else {
        setPinError('Invalid PIN');
      }
    } catch {
      setPinError('Invalid PIN');
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#2D5A27', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999, color: 'white', textAlign: 'center',
    }}>
      <div>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Thank you for signing!</h2>
        <p style={{ opacity: 0.8, marginBottom: '2rem' }}>Please return the device to the administrator.</p>
        <button
          className="agreements-btn"
          style={{ background: 'white', color: '#2D5A27', padding: '0.75rem 2rem', fontSize: '1rem', fontWeight: 600 }}
          onClick={() => setShowPinModal(true)}
        >
          Back to Admin
        </button>
      </div>

      {showPinModal && (
        <div className="pin-modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="pin-modal" onClick={e => e.stopPropagation()}>
            <h3>Enter Admin PIN</h3>
            <input
              type="password"
              value={pin}
              onChange={e => setPin(e.target.value)}
              maxLength={8}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleVerifyPin()}
            />
            {pinError && <div className="pin-error">{pinError}</div>}
            <div className="agreements-btn-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
              <button className="agreements-btn agreements-btn-primary" onClick={handleVerifyPin} disabled={!pin}>
                Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgreementsAdmin;
